import { BadgeAdapter } from 'stores/adapters/bex-api/search';

import { BadgeModel, HotelMapModel, MapMarker, SheetModel } from 'models';
import { MarkerIcon } from 'models/hotel/map-types/map-marker';
import { PropertySearchListingViewModel } from 'models/hotel/search-types';

import { updateSlotsParamForMapTravelAds } from 'utils/infosite-url-utils';

import { MapIconType, PropertyFields, Theme } from '__generated__/api';

export const mergePOIsAndHotelMarkers = (map: HotelMapModel, propertySearchListings: PropertySearchListingViewModel[]): MapMarker[] => {
  const mapMarkers = map && map.markers;

  const poiMarkers = getMapPoiMarkers(mapMarkers);
  const propertyMarkers = getListingPropertyMarkers(propertySearchListings);

  return propertyMarkers.concat(poiMarkers);
};

export const getSheet = (property: PropertyFields.Fragment): SheetModel | null => {
  let sponsoredBadge: BadgeModel = null;
  let badge: BadgeModel;
  if (property.offerBadge?.primary?.theme_temp === Theme.SPONSORED) {
    sponsoredBadge = BadgeAdapter.getBadge(property.offerBadge?.primary);
    badge = BadgeAdapter.getBadge(property.offerBadge?.secondary);
  } else {
    const primaryBadge = property.offerBadge?.primary;
    const secondaryBadge = property.offerBadge?.secondary;
    badge = primaryBadge ? BadgeAdapter.getBadge(primaryBadge) : BadgeAdapter.getBadge(secondaryBadge);
  }

  const linkOff = property.sponsoredListing
    ? updateSlotsParamForMapTravelAds(property.propertyDetailsLink?.uri?.relativePath)
    : property.propertyDetailsLink?.uri?.relativePath;

  const price = property.price;
  const lead = price && price.lead && price.lead.formatted;

  return {
    primaryBadge: sponsoredBadge,
    secondaryBadgeText: badge.text,
    secondaryBadgeTheme: badge.theme,
    distanceFromPOI: property.destinationInfo && property.destinationInfo.distanceFromMessaging,
    id: Number(property.id),
    leadPrice: property.mapMarker.icon === MapIconType.SOLD_OUT ? null : lead,
    linkOff,
    photoUrl: property.propertyImage?.image?.url,
    priceMessages: property.price && property.price.priceMessages && property.price.priceMessages.map(message => message.value),
    title: property.name,
    strikeoutPrice: price && price.strikeOut && price.strikeOut.formatted,
    strikeoutDisclaimer: price && price.disclaimer && price.disclaimer.value,
    subtitle: property.reviews && property.reviews.localizedSubtitle,
    vipMessage: property.vipMessaging,
    options: price && price.options,
    travelAd: property.sponsoredListing,
  };
};

const getMapPoiMarkers = (mapMarkers: any): MapMarker[] => {
  const poiMarkers = mapMarkers && mapMarkers.filter(marker => marker.__typename === 'PointOfInterest');
  if (!poiMarkers || poiMarkers.length === 0) {
    return [];
  }

  return poiMarkers.map(marker => ({
    position: {
      lat: marker.mapMarker.latLong?.latitude,
      lng: marker.mapMarker.latLong?.longitude,
    },
    label: marker.mapMarker.label,
    icon: MarkerIcon[marker.mapMarker.icon],
    sheet: { title: marker.name, id: -1 },
    clientSideAnalytics: {
      refererId: marker.mapMarker.clientSideAnalytics?.referrerId,
      linkname: marker.mapMarker.clientSideAnalytics?.linkName,
    },
  }));
};

const getListingPropertyMarkers = (propertySearchListings: PropertySearchListingViewModel[]): MapMarker[] => {
  const properties = getPropertiesForMapMarkers(propertySearchListings);

  if (!properties || properties.length === 0) {
    return [];
  }

  return properties
    .filter(property => property.mapMarker)
    .map(property => ({
      position: {
        lat: property.mapMarker.latLong.latitude,
        lng: property.mapMarker.latLong.longitude,
      },
      label: property.mapMarker.label,
      icon: MarkerIcon[property.mapMarker.icon],
      sheet: getSheet(property),
      clientSideAnalytics: {
        refererId: property.mapMarker.clientSideAnalytics?.referrerId,
        linkname: property.mapMarker.clientSideAnalytics?.linkName,
      },
    }));
};

const getPropertiesForMapMarkers = (listings: PropertySearchListingViewModel[]): PropertyFields.Fragment[] => {
  const properties = getPropertiesFromListings(listings);

  const propertyIds = properties && getPropertyIdsWithTravelAdsLinkin(properties);

  return (
    properties &&
    properties.filter(
      property =>
        property?.sponsoredListing?.clickRedirectLinkoffTrackingUrl === null ||
        (!property?.sponsoredListing && !propertyIds.includes(property.id))
    )
  );
};

const getPropertiesFromListings = (listings: PropertySearchListingViewModel[]): PropertyFields.Fragment[] =>
  listings &&
  listings
    .filter(listing => {
      const model = listing.model as PropertyFields.Fragment;

      return model.__typename === 'Property' && !isZeroCoordinates(model);
    })
    .map(listing => listing.model as PropertyFields.Fragment);

const getPropertyIdsWithTravelAdsLinkin = (properties: PropertyFields.Fragment[]): string[] =>
  properties
    .filter(property => property?.sponsoredListing && !property?.sponsoredListing.clickRedirectTrackingUrl)
    .map(property => property.id);

const isZeroCoordinates = (property: PropertyFields.Fragment): boolean =>
  property?.mapMarker?.latLong.latitude === 0 && property?.mapMarker?.latLong.longitude === 0;
